import MaterialTabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import {Link, useLocation} from 'react-router-dom';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useAppSelector} from '../../store/hooks';

type TabProps = {
  name: string,
  link: string,
  permissions: Nullable<string[]>
}

type MenuProps = {
  tabs: TabProps[],
  className?: string,
  orientation?: "vertical" | "horizontal"
}

const getTabsStyles = (orientation) => {
  const border = orientation === 'horizontal' ? {borderBottom: 1} : {borderRight: 1};

  return {
    ...border,
    width: 'max-content',
    height: '100%',
    borderColor: 'divider'
  };
};

const getInitialValue = (tabs, path) => {
  const tabIndex = tabs.findIndex((tab) => tab.link === path);

  return tabIndex === -1 ? 0 : tabIndex;
};

const Tabs = ({tabs, className='tabs', orientation = 'horizontal'}: MenuProps) => {
  const {permissions} = useAppSelector((state) => state.permissions);
  const {pathname} = useLocation();
  const initialValue = useMemo(() =>
    getInitialValue(tabs, pathname),
  [tabs, pathname]
  );
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleChange = useCallback((_, newValue) => {
    setValue(newValue);
  }, []);

  const tabStyles = useMemo(() => {
    return getTabsStyles(orientation);
  }, [orientation]);

  const resultTabs = useMemo(() =>
    tabs.filter(({permissions: tabPermissions}) => {
      if (!tabPermissions) {
        return true;
      }

      return tabPermissions?.some(
          (permission) => permissions.includes(permission)
      );
    }), [tabs]);

  return (
    <MaterialTabs
      value={value}
      className={className}
      orientation={orientation}
      onChange={handleChange}
      sx={tabStyles}
    >
      {resultTabs.map(({name, link}, id) => (
        <Tab
          label={name}
          value={id}
          to={link}
          component={Link}
          key={name}
        />
      ))}
    </MaterialTabs>
  );
};

export default Tabs;
